/**
 * useTelemetryReporter — periodically (and on monthly close) ships a compact
 * snapshot of the player's game state to the backend so a supervisor can
 * see live progress without accessing the player's machine.
 *
 * Hook behaviour:
 *   - Reads the current license key from licenseStore.
 *   - Builds a snapshot from the live gameStore state.
 *   - POSTs to /api/telemetry/snapshot on:
 *       1. game start (first call after gameStarted flips true)
 *       2. each monthly close (when month index changes)
 *       3. day delta ≥ 3 in-game days since the last send (debounced)
 *       4. fallback: every 2 minutes wall-clock if nothing else triggered it
 *       5. beforeunload: final beacon to capture the latest state when the
 *          player closes the tab (uses navigator.sendBeacon for reliability
 *          across Safari/Firefox).
 *   - Logs fetch failures via console.warn so production telemetry
 *     issues can be diagnosed (silent failures masked the Q1 bug where
 *     the supervisor view filters on total_days > 0 and a day-0 snapshot
 *     made every player look "not playing" until next-month tick).
 *
 * 11/02/2026 (Itération 92) — Bug user prod : « Plusieurs apprenants jouent
 * sur Safari Mac mais aucune donnée ne remonte ». Le transport utilisait
 * `fetch keepalive: true` qui sur Safari dépasse silencieusement le quota
 * 64KB par session et coupe tous les snapshots suivants. Refonte du
 * transport : retry exponentiel, queue persistante, sendBeacon réservé
 * au `beforeunload`. Voir helpers/telemetryTransport.js.
 */
import { useEffect, useRef } from 'react';
import useGameStore from '../store/gameStore';
import useLicenseStore from '../store/licenseStore';
import useSaveStore from '../store/saveStore';
import { computeBalanceSheet } from '../utils/balanceSheet';
import { computeAdvisorInsights } from '../utils/advisorInsights';
import { buildContractAcceptanceStats } from '../store/helpers/contractAcceptanceStats';
import { buildStatementSnapshotFields } from '../utils/exerciseStatements';
import {
  sendTelemetrySnapshot,
  flushTelemetryQueue,
  sendTelemetryBeacon,
} from './telemetryTransport';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes wall-clock
const DAY_HEARTBEAT_THRESHOLD = 3; // send if totalDays advanced ≥ 3 since last send
const MIN_WALL_CLOCK_BETWEEN_SENDS_MS = 15 * 1000; // throttle: max 1 send / 15s
const QUEUE_FLUSH_INTERVAL_MS = 5 * 60 * 1000; // try to flush queue every 5 min

/**
 * Extract a compact summary of every used save slot so the supervisor can
 * see which of the 3 client-side slots an apprentice is actually using.
 * We never ship the full game data — only headline KPIs already covered
 * by the live snapshot.
 */
function buildSaveSlotsSummary() {
  try {
    const st = useSaveStore.getState();
    const slots = st.getSlots(); // [{key, save | null, favorite}, ...]
    return slots
      .filter((s) => s.save)
      .map((s) => ({
        slot_key: s.key,
        name: s.save.name || '',
        total_days: s.save.totalDays || 0,
        treasury: Math.round(s.save.treasury || 0),
        vehicle_count: s.save.vehicleCount || 0,
        driver_count: s.save.driverCount || 0,
        saved_at: s.save.date || null,
        // 17/07/2026 — slot mis en avant par l'apprenant (⭐).
        favorite: !!s.favorite,
        // 18/07/2026 — note pédagogique jointe au slot favori.
        favorite_note: s.favorite ? (st.favoriteNote || '') : '',
      }));
  } catch (_) {
    return [];
  }
}

// export nommé pour les tests (fiche pédagogique par partie, 28/07/2026).
export function buildSnapshot(state, licenseKey) {
  const sheet = computeBalanceSheet(state);
  const drivers = state.drivers || [];
  const vehicles = state.vehicles || [];
  const credits = state.credits || [];
  // 18/06/2026 — fix prod : hiredSupport est un OBJET ({roleId: true}),
  // pas un tableau. L'ancien `.map` crashait le snapshot télémétrie
  // pendant createCompany.
  const hiredSupportRoles = Array.isArray(state.hiredSupport)
    ? state.hiredSupport.map(s => s?.id || s)
    : Object.keys(state.hiredSupport || {});
  const insights = computeAdvisorInsights(state) || [];
  const avgSat = drivers.length > 0
    ? Math.round(drivers.reduce((a, d) => a + (d.satisfaction || 0), 0) / drivers.length)
    : 0;
  // 04/06/2026 — Extra fields for the pedagogical PDF profile sheet
  // (Super Vue admin → "📄 Fiche PDF"). Cheap to compute, sent every ping.
  const avgCompetence = drivers.length > 0
    ? +(drivers.reduce((a, d) => a + (d.competence || 0), 0) / drivers.length).toFixed(1)
    : 0;
  const totalTrainings = drivers.reduce((a, d) => a + ((d.trainings || []).length), 0);
  const totalDebt = credits.reduce((a, c) => a + (c.remainingPrincipal || 0), 0);
  const electricCount = vehicles.filter(v => v.engineType === 'electric').length;
  const fatigueAvg = drivers.length > 0
    ? Math.round(drivers.reduce((a, d) => a + (d.fatigue || 0), 0) / drivers.length)
    : 0;
  // 17/06/2026 (Lot 6) — Snapshot diplomatie : moyenne des relations, comptage
  // alliés/ennemis, décisions narratives prises, fonction support juridique.
  const relations = Object.values(state.competitorRelations || {});
  const relAvg = relations.length > 0
    ? Math.round(relations.reduce((a, v) => a + (v || 0), 0) / relations.length)
    : 50;
  const allies = relations.filter(v => v >= 56).length;
  const enemies = relations.filter(v => v <= 35).length;
  const relationDecisions = (state.inbox || []).filter(
    m => m.senderType === 'competitor' && m.replied,
  ).length;
  const narrativeFlagsCount = Object.keys(state.narrativeFlags || {}).length;
  const legalCounselHired = !!((state.hiredSupport || {})['role-juliette-charron-berthet']);
  // 08/02/2026 — Q2 milestone emails étendus : on remonte le nombre d'AO
  // nationaux gagnés ET le nombre de concurrents rachetés. Le backend
  // compare au snapshot précédent et envoie un email au formateur à
  // chaque NOUVELLE occurrence (option `notify_milestones`).
  const tenderHistory = state.tenderHistory || [];
  const nationalWins = tenderHistory.filter(t => t.outcome === 'won' && t.tier === 'national');
  const lastNationalWin = nationalWins[0] || null; // most recent first
  const acquiredCompetitorIds = state.acquiredCompetitorIds || [];
  // Latest competitor name : look up the last id against `state.competitors`
  // (or fallback to the id). The Pydantic v2 snapshot accepts extra fields,
  // so we ship a string label the backend can drop straight into the email.
  let lastCompetitorAcquiredName = '';
  if (acquiredCompetitorIds.length > 0) {
    const lastId = acquiredCompetitorIds[acquiredCompetitorIds.length - 1];
    const c = (state.competitors || []).find(cc => cc.id === lastId);
    lastCompetitorAcquiredName = c?.name || lastId;
  }
  // 28/07/2026 — Fiche apprenant par partie + bilan économique.
  // Dernier mois CLOS (le compteur monthlyRevenues du mois en cours est
  // partiel — c'était la cause du « CA mensuel < charges » sur la fiche).
  const monthlyHistory = state.monthlyHistory || [];
  const lastMonth = monthlyHistory[monthlyHistory.length - 1] || null;
  const currentYear = state.year || 1;
  const tvaPaidYtd = (state.tvaHistory || [])
    .filter(e => e.year === currentYear)
    .reduce((s, e) => s + (e.paid || 0), 0);
  const isPaidYtd = (state.isInstallmentsPaid || [])
    .filter(e => e.year === currentYear)
    .reduce((s, e) => s + (e.amount || 0), 0);
  const essieuPaidYtd = (state.essieuPaid || [])
    .filter(e => (e.year || currentYear) === currentYear)
    .reduce((s, e) => s + (e.amount || 0), 0);
  const aleasYtd = (state.eventLog || []).filter(
    e => ((e.resolvedAt && e.resolvedAt.year) || 0) === currentYear && (e.eventCost || 0) > 0,
  );
  let contractsRecap = null;
  try {
    const stats = buildContractAcceptanceStats(state, state.totalDays || 0);
    if (stats) {
      contractsRecap = {
        total_taken: stats.totalTaken,
        total_missed: stats.totalMissed,
        acceptance_rate: stats.acceptanceRate,
        rows: stats.rows.map(r => ({ id: r.id, label: r.label, taken: r.taken, missed: r.missed, rate: r.rate })),
      };
    }
  } catch (_) { /* best-effort — la fiche dégradera proprement */ }
  // 21/09/2026 — Bilans d'exercice (clos + en cours provisoire) pour la
  // supervision formateur : même structure que Finance › Historique.
  let statementFields = { yearly_statements: [], current_year_statement: null };
  try {
    statementFields = buildStatementSnapshotFields(state);
  } catch (_) { /* best-effort */ }
  return {
    license_key: licenseKey,
    company_name: state.company?.name || '',
    company_city: state.company?.city || '',
    company_specialty: state.company?.specialty || '',
    // 19/07/2026 — les logos personnalisés sont des objets → on n'envoie
    // que les identifiants string (le serveur rejetait le snapshot en 422).
    company_logo: typeof state.company?.logo === 'string' ? state.company.logo : null,
    total_days: state.totalDays || 0,
    // 20/07/2026 — temps de jeu réel cumulé (secondes).
    play_time_seconds: Math.round(state.playTimeSeconds || 0),
    year: state.year || 1,
    treasury: Math.round(state.treasury || 0),
    monthly_revenue: Math.round(state.monthlyRevenues || 0),
    monthly_expenses: Math.round(state.monthlyExpenses || 0),
    equity: Math.round(sheet.equity || 0),
    actif_total: Math.round(sheet.actif?.total || 0),
    passif_total: Math.round(sheet.passif?.total || 0),
    fleet_total: vehicles.length,
    fleet_active: vehicles.filter(v => v.status === 'active').length,
    fleet_leased: vehicles.filter(v => v.isLeased).length,
    fleet_mortgaged: vehicles.filter(v => v.mortgaged).length,
    fleet_electric: electricCount,
    depot_phase: state.depotPhase || 0,
    drivers_total: drivers.length,
    drivers_avg_satisfaction: avgSat,
    drivers_avg_competence: avgCompetence,
    drivers_avg_fatigue: fatigueAvg,
    drivers_trainings_total: totalTrainings,
    support_total: hiredSupportRoles.length,
    support_roles: hiredSupportRoles.slice(0, 16),
    credits_count: credits.length,
    credits_total_remaining: Math.round(totalDebt),
    // 18/06/2026 — fix prod 422 : creditScore devient un OBJET {score, grade}
    // après calcul (bankSlice) alors que le backend attend Optional[int].
    // 20/07/2026 — blindage total : certaines saves prod portent une CHAÎNE
    // (grade legacy) → on n'envoie qu'un entier fini, sinon null.
    credit_score: (() => {
      const raw = (state.creditScore && typeof state.creditScore === 'object')
        ? state.creditScore.score
        : state.creditScore;
      const n = Number(raw);
      return Number.isFinite(n) ? Math.round(n) : null;
    })(),
    reputation: Math.round(state.reputation || 0),
    notoriety: Math.round(state.notoriety || 0),
    contracts_active: (state.activeContracts || []).length,
    contracts_completed_lifetime: state.contractsCompletedLifetime || 0,
    phase: state.phase || 1,
    client_loyalty_count: Object.keys(state.clientLoyalty || {}).length,
    insights_top: insights.slice(0, 3).map(i => i.id),
    advisor_id: state.selectedAdvisor || null,
    // 17/06/2026 (Lot 6) — Diplomatie / relations externes.
    competitor_relations_avg: relAvg,
    competitor_allies_count: allies,
    competitor_enemies_count: enemies,
    relation_events_decided: relationDecisions,
    narrative_flags_count: narrativeFlagsCount,
    legal_counsel_hired: legalCounselHired,
    // 08/02/2026 — Milestone email extensions.
    tenders_national_won: nationalWins.length,
    last_national_tender_client: lastNationalWin?.clientName || '',
    competitors_bought_out: acquiredCompetitorIds.length,
    last_competitor_acquired: lastCompetitorAcquiredName,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : null,
    save_slots: buildSaveSlotsSummary(),
    // 28/07/2026 — Fiche apprenant par partie + bilan économique.
    game_run_id: state.gameRunId || null,
    difficulty: state.difficulty || 'normal',
    last_month: lastMonth ? {
      month: lastMonth.month, year: lastMonth.year,
      revenue: lastMonth.totalRevenue || 0,
      expenses: lastMonth.totalExpenses || 0,
      net: lastMonth.netResult || 0,
    } : null,
    monthly_series: monthlyHistory.slice(-12).map(m => ({
      month: m.month, year: m.year,
      revenue: m.totalRevenue || 0, expenses: m.totalExpenses || 0, net: m.netResult || 0,
    })),
    expenses_by_poste: (lastMonth && lastMonth.expensesByPoste) || null,
    taxes_ytd: {
      tva: Math.round(tvaPaidYtd),
      is: Math.round(isPaidYtd),
      essieu: Math.round(essieuPaidYtd),
    },
    aleas_ytd: {
      cost: Math.round(aleasYtd.reduce((s, e) => s + (e.eventCost || 0), 0)),
      count: aleasYtd.length,
    },
    contracts_recap: contractsRecap,
    yearly_statements: statementFields.yearly_statements,
    current_year_statement: statementFields.current_year_statement,
  };
}

async function sendSnapshot(state, licenseKey) {
  if (!licenseKey || !state.gameStarted || !BACKEND) return;
  // 11/02/2026 — Délégué au transport robuste (retry + queue persistante).
  // Voir hooks/telemetryTransport.js pour le détail.
  await sendTelemetrySnapshot(BACKEND, buildSnapshot(state, licenseKey));
}

/**
 * 04/06/2026 — Poll the backend for any messages that a supervisor queued
 * for this license (e.g. "📄 PDF" feature). On hit, push each into the
 * in-game inbox via `pushCustomMessage`.
 *
 * The backend marks each delivered message as `delivered: true` before
 * returning it, so we never double-deliver — even if the player has
 * multiple tabs open or the function is called twice in a tight loop.
 */
async function pollPendingMessages(licenseKey) {
  if (!licenseKey || !BACKEND) return;
  try {
    const url = `${BACKEND}/api/telemetry/pending-messages?license_key=${encodeURIComponent(licenseKey)}`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return;
    const data = await res.json();
    const messages = Array.isArray(data?.messages) ? data.messages : [];
    if (messages.length === 0) return;
    const push = useGameStore.getState().pushCustomMessage;
    if (typeof push !== 'function') return;
    messages.forEach(m => {
      try {
        push({
          senderType: 'master',
          senderName: m.sender_name || 'Responsable pédagogique',
          tone: m.kind === 'warning' ? 'warning' : (m.kind === 'positive' ? 'positive' : 'neutral'),
          subject: m.subject,
          body: m.body,
          kind: m.kind || 'info',
          ttlDays: 21,
        });
      } catch (_) { /* swallow per-message errors */ }
    });
  } catch (_) {
    /* offline / unreachable — silent */
  }
}

export default function useTelemetryReporter() {
  const licenseKey = useLicenseStore(s => s.licenseKey);
  const monthRef = useRef(null);
  const startSentRef = useRef(false);
  const lastSentDayRef = useRef(null);
  const lastSentAtRef = useRef(0);

  // Effect 1: subscribe to month changes and gameStarted transitions.
  useEffect(() => {
    if (!licenseKey) return undefined;

    // Helper: send + bookkeeping. Throttled to MIN_WALL_CLOCK_BETWEEN_SENDS_MS
    // so a multi-day batch tick (e.g., player holds nextDay) doesn't spam.
    const trySend = (state, force = false) => {
      const now = Date.now();
      if (!force && now - lastSentAtRef.current < MIN_WALL_CLOCK_BETWEEN_SENDS_MS) {
        return;
      }
      lastSentAtRef.current = now;
      lastSentDayRef.current = state.totalDays || 0;
      monthRef.current = `${state.year}-${state.month}`;
      sendSnapshot(state, licenseKey);
    };

    // Initial push on mount if game already started (rehydrated session).
    const initialState = useGameStore.getState();
    if (initialState.gameStarted && !startSentRef.current) {
      startSentRef.current = true;
      trySend(initialState, true /* force first send */);
    }

    const unsubscribe = useGameStore.subscribe((state) => {
      if (!state.gameStarted) return;
      const tag = `${state.year}-${state.month}`;
      const day = state.totalDays || 0;

      // 1. First time we see gameStarted true (CreateCompany → start flow).
      if (!startSentRef.current) {
        startSentRef.current = true;
        trySend(state, true);
        return;
      }
      // 2. Monthly close detection (force-send: critical pedagogical snapshot).
      if (monthRef.current && tag !== monthRef.current) {
        trySend(state, true);
        return;
      }
      // 3. Day heartbeat: send if ≥ N in-game days elapsed since last send.
      //    This fixes the Q1 bug where supervisors saw all licenses as "not
      //    playing" because the only snapshot ever sent was the day-0 one.
      if (
        lastSentDayRef.current !== null
        && day - lastSentDayRef.current >= DAY_HEARTBEAT_THRESHOLD
      ) {
        trySend(state, false);
      }
    });
    return unsubscribe;
  }, [licenseKey]);

  // Effect 2: 2-minute wall-clock fallback in case the player is idle but online.
  useEffect(() => {
    if (!licenseKey) return undefined;
    const id = setInterval(() => {
      const state = useGameStore.getState();
      if (state.gameStarted) {
        lastSentAtRef.current = Date.now();
        lastSentDayRef.current = state.totalDays || 0;
        monthRef.current = `${state.year}-${state.month}`;
        sendSnapshot(state, licenseKey);
      }
    }, FALLBACK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [licenseKey]);

  // Effect 3: poll for supervisor-queued messages (04/06/2026, "Fiche PDF").
  // Once on mount + every 60s. Runs even if the game is not yet started, so
  // a player who just opened the app sees the master's message immediately.
  useEffect(() => {
    if (!licenseKey) return undefined;
    pollPendingMessages(licenseKey); // initial
    const id = setInterval(() => pollPendingMessages(licenseKey), 60_000);
    return () => clearInterval(id);
  }, [licenseKey]);

  // Effect 4 (11/02/2026 — Itération 92) : flush la file persistée des
  // snapshots qui ont échoué dans une session précédente (typique des
  // apprenants Safari Mac dont le quota keepalive avait saturé).
  // Tente toutes les 5 min + au montage.
  useEffect(() => {
    if (!licenseKey || !BACKEND) return undefined;
    flushTelemetryQueue(BACKEND); // initial
    const id = setInterval(() => flushTelemetryQueue(BACKEND), QUEUE_FLUSH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [licenseKey]);

  // Effect 5 (11/02/2026 — Itération 92) : `beforeunload` beacon.
  // Envoi final via `navigator.sendBeacon` quand l'apprenant ferme l'onglet.
  // C'est le seul transport fiable à l'unload (fetch/keepalive échouent
  // sur Safari/Firefox). Garantit qu'on capture l'état FINAL même si
  // l'apprenant ferme brutalement.
  useEffect(() => {
    if (!licenseKey || !BACKEND) return undefined;
    const handler = () => {
      const state = useGameStore.getState();
      if (!state.gameStarted) return;
      try {
        sendTelemetryBeacon(BACKEND, buildSnapshot(state, licenseKey));
      } catch (err) {
        console.warn('[telemetry] beforeunload beacon failed:', err?.message || err);
      }
    };
    window.addEventListener('beforeunload', handler);
    // `pagehide` is the Safari/iOS-friendly equivalent — fires on tab
    // close, navigation away AND when the page enters the back-forward
    // cache. We listen to both to maximise capture rate.
    window.addEventListener('pagehide', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      window.removeEventListener('pagehide', handler);
    };
  }, [licenseKey]);

  // Effect 6 (18/07/2026) — heartbeat « app ouverte » : ping léger envoyé
  // une fois par chargement dès que la licence est validée, AVANT même la
  // création de société. Permet au diagnostic Super Vue de distinguer
  // « jamais ouvert le jeu » de « télémétrie de jeu bloquée ».
  useEffect(() => {
    if (!licenseKey || !BACKEND) return undefined;
    fetch(`${BACKEND}/api/telemetry/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        license_key: licenseKey,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
    }).catch(() => { /* diagnostic best-effort */ });
    return undefined;
  }, [licenseKey]);

  // Effect 7 (20/07/2026) — temps de jeu réel : +30 s toutes les 30 s tant
  // que la partie est lancée et l'onglet visible. Persisté avec la partie,
  // remonté en supervision via play_time_seconds.
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const st = useGameStore.getState();
        if (!st.gameStarted) return;
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        useGameStore.setState({ playTimeSeconds: (st.playTimeSeconds || 0) + 30 });
      } catch (_) { /* no-op */ }
    }, 30000);
    return () => clearInterval(id);
  }, []);
}
